import React, { useState, useEffect } from 'react';
import {
	Box,
	Flex,
	Heading,
	Text,
	VStack,
	SimpleGrid,
	Icon,
	HStack,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Badge,
	Link,
	Spinner,
	Center,
	Card,
	CardBody,
	Button,
	Divider,
} from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import {
	FaDollarSign,
	FaBoxOpen,
	FaUsers,
	FaBuilding,
	FaShoppingCart,
	FaClock,
	FaChartBar,
	FaArrowRight,
} from 'react-icons/fa';
import {
	AreaChart,
	Area,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from 'recharts';
import { connect } from 'react-redux';
import axios from 'axios';
import AdminNavbar from '../../component/adminNavbar';
import { getAllAdminOrders } from '../../redux/actions/orderActions';
import {
	getWeeklyySalesReport,
	getTodaysSales,
} from '../../redux/actions/salesActions';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

const authHeaders = () => ({
	headers: { 'x-auth-token': localStorage.getItem('x-auth-token') },
});

const fmt = (n: number) =>
	new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(n || 0);

const statusColor = (status: string) => {
	const s = status?.toLowerCase();
	if (s === 'completed') return 'green';
	if (s === 'pending') return 'yellow';
	if (s === 'canceled') return 'red';
	if (s === 'shipped') return 'blue';
	return 'gray';
};

const greeting = () => {
	const h = new Date().getHours();
	if (h < 12) return 'Good morning';
	if (h < 17) return 'Good afternoon';
	return 'Good evening';
};

const KpiCard = ({ icon, label, value, color, to }: any) => (
	<NavLink to={to || '#'} style={{ textDecoration: 'none' }}>
		<Card
			shadow="sm"
			borderRadius="xl"
			bg="white"
			transition="all 0.2s"
			_hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
			cursor="pointer"
		>
			<CardBody>
				<Flex
					w="42px"
					h="42px"
					borderRadius="lg"
					bg={`${color}.50`}
					align="center"
					justify="center"
					mb={3}
				>
					<Icon as={icon} boxSize={5} color={`${color}.500`} />
				</Flex>
				<Text color="gray.500" fontSize="xs" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={1}>
					{label}
				</Text>
				<Text fontSize="xl" fontWeight="bold" color="gray.800" lineHeight="short">
					{value}
				</Text>
			</CardBody>
		</Card>
	</NavLink>
);

const QuickLink = ({ icon, label, to, color }: any) => (
	<NavLink to={to} style={{ textDecoration: 'none' }}>
		<Flex
			align="center"
			justify="space-between"
			px={4}
			py={3}
			borderRadius="lg"
			border="1px solid"
			borderColor="gray.100"
			bg="white"
			transition="all 0.15s"
			_hover={{ bg: `${color}.50`, borderColor: `${color}.200` }}
		>
			<HStack spacing={3}>
				<Flex
					w="32px"
					h="32px"
					borderRadius="md"
					bg={`${color}.100`}
					align="center"
					justify="center"
				>
					<Icon as={icon} boxSize={4} color={`${color}.600`} />
				</Flex>
				<Text fontSize="sm" fontWeight="medium" color="gray.700">{label}</Text>
			</HStack>
			<Icon as={FaArrowRight} boxSize={3} color="gray.400" />
		</Flex>
	</NavLink>
);

const Admin = ({
	adminorder,
	adminorderloading,
	report,
	todaysTotal,
	getAllAdminOrders,
	getTodaysSales,
	getWeeklyySalesReport,
}: any) => {
	const [summary, setSummary] = useState<any>(null);
	const [summaryLoading, setSummaryLoading] = useState(true);

	useEffect(() => {
		getTodaysSales();
		getAllAdminOrders(0);
		getWeeklyySalesReport();
		axios
			.get(`${BASE_URL}/api/reports/summary`, authHeaders())
			.then((r) => setSummary(r.data))
			.finally(() => setSummaryLoading(false));
	}, []);

	const formatChartData = (weeklyReport: any[]) => {
		if (!weeklyReport || !Array.isArray(weeklyReport)) return [];
		const salesMap = new Map<string, number>();
		for (let i = 6; i >= 0; i--) {
			const d = new Date();
			d.setDate(d.getDate() - i);
			salesMap.set(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), 0);
		}
		weeklyReport.forEach((sale) => {
			const label = new Date(sale.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
			salesMap.set(label, parseFloat(sale.total || '0'));
		});
		return Array.from(salesMap, ([name, Sales]) => ({ name, Sales }));
	};

	const chartData = formatChartData(report);
	const todaySales = parseFloat(todaysTotal || '0');
	const pendingOrders = summary?.pending_orders ?? '—';
	const totalOrders = summary?.total_orders ?? '—';
	const activeProducts = summary?.active_products ?? '—';
	const activeUsers = summary?.active_users ?? '—';
	const canceledOrders = summary?.canceled_orders ?? '—';

	const today = new Date().toLocaleDateString('en-US', {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});

	return (
		<>
			<AdminNavbar />
			<Box pt={{ base: 16, md: 6 }} px={{ base: 4, md: 6 }} pb={6} ml={{ base: 0, md: "250px" }} bg="gray.100" minH="100vh">
				{/* Header */}
				<Flex justify="space-between" align="flex-start" mb={6}>
					<Box>
						<Heading size="lg" color="gray.800" mb={1}>
							{greeting()}, Admin
						</Heading>
						<Text color="gray.500" fontSize="sm">{today}</Text>
					</Box>
					<Button
						as={NavLink}
						to="/admin/reports"
						leftIcon={<Icon as={FaChartBar} />}
						colorScheme="blue"
						size="sm"
						variant="outline"
					>
						Full Reports
					</Button>
				</Flex>

				{/* KPI Cards */}
				{summaryLoading ? (
					<Center py={8}><Spinner color="blue.500" size="lg" /></Center>
				) : (
					<SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4} mb={6}>
						<KpiCard
							icon={FaDollarSign}
							label="Today's Sales"
							value={fmt(todaySales)}
							color="green"
							to="/admin/orders"
						/>
						<KpiCard
							icon={FaShoppingCart}
							label="Total Orders"
							value={totalOrders}
							color="blue"
							to="/admin/orders"
						/>
						<KpiCard
							icon={FaClock}
							label="Pending Orders"
							value={pendingOrders}
							color="yellow"
							to="/admin/orders"
						/>
						<KpiCard
							icon={FaBoxOpen}
							label="Active Products"
							value={activeProducts}
							color="purple"
							to="/admin/products"
						/>
						<KpiCard
							icon={FaUsers}
							label="Active Users"
							value={activeUsers}
							color="teal"
							to="/admin/users"
						/>
					</SimpleGrid>
				)}

				{/* Chart + Quick Links */}
				<SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} mb={6}>
					{/* Sales Chart */}
					<Box gridColumn={{ lg: 'span 2' }}>
						<Card shadow="sm" borderRadius="xl" bg="white" overflow="hidden">
							<Box px={6} py={4} borderBottomWidth="1px" borderColor="gray.100">
								<Heading size="sm" color="gray.700">Revenue — Last 7 Days</Heading>
								<Text fontSize="xs" color="gray.400" mt={0.5}>Based on completed orders</Text>
							</Box>
							<CardBody pt={4} pb={2}>
								<ResponsiveContainer width="100%" height={220}>
									<AreaChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
										<defs>
											<linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
												<stop offset="5%" stopColor="#3182CE" stopOpacity={0.15} />
												<stop offset="95%" stopColor="#3182CE" stopOpacity={0} />
											</linearGradient>
										</defs>
										<CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
										<XAxis dataKey="name" tick={{ fontSize: 11, fill: '#718096' }} axisLine={false} tickLine={false} />
										<YAxis tick={{ fontSize: 11, fill: '#718096' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
										<Tooltip
											formatter={(v: number) => [fmt(v), 'Sales']}
											contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', fontSize: '12px' }}
										/>
										<Area type="monotone" dataKey="Sales" stroke="#3182CE" strokeWidth={2} fillOpacity={1} fill="url(#colorSales)" dot={{ r: 3, fill: '#3182CE' }} activeDot={{ r: 5 }} />
									</AreaChart>
								</ResponsiveContainer>
							</CardBody>
						</Card>
					</Box>

					{/* Quick Links */}
					<Card shadow="sm" borderRadius="xl" bg="white" overflow="hidden">
						<Box px={6} py={4} borderBottomWidth="1px" borderColor="gray.100">
							<Heading size="sm" color="gray.700">Quick Actions</Heading>
						</Box>
						<CardBody>
							<VStack spacing={2} align="stretch">
								<QuickLink icon={FaShoppingCart} label="Manage Orders" to="/admin/orders" color="blue" />
								<QuickLink icon={FaBoxOpen} label="Manage Products" to="/admin/products" color="purple" />
								<QuickLink icon={FaUsers} label="Manage Users" to="/admin/users" color="teal" />
								<QuickLink icon={FaBuilding} label="Manage Facilities" to="/admin/facilities" color="orange" />
								<Divider />
								<Box pt={1}>
									<Text fontSize="xs" color="gray.400" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={2}>
										Order Snapshot
									</Text>
									<SimpleGrid columns={2} spacing={2}>
										<Box bg="yellow.50" borderRadius="lg" p={3} textAlign="center">
											<Text fontSize="lg" fontWeight="bold" color="yellow.700">{pendingOrders}</Text>
											<Text fontSize="xs" color="yellow.600">Pending</Text>
										</Box>
										<Box bg="red.50" borderRadius="lg" p={3} textAlign="center">
											<Text fontSize="lg" fontWeight="bold" color="red.700">{canceledOrders}</Text>
											<Text fontSize="xs" color="red.600">Canceled</Text>
										</Box>
									</SimpleGrid>
								</Box>
							</VStack>
						</CardBody>
					</Card>
				</SimpleGrid>

				{/* Recent Orders */}
				<Card shadow="sm" borderRadius="xl" bg="white" overflow="hidden">
					<Box px={6} py={4} borderBottomWidth="1px" borderColor="gray.100">
						<Flex justify="space-between" align="center">
							<Box>
								<Heading size="sm" color="gray.700">Recent Orders</Heading>
								<Text fontSize="xs" color="gray.400" mt={0.5}>Last 5 orders placed</Text>
							</Box>
							<Link as={NavLink} to="/admin/orders" fontSize="sm" color="blue.500" fontWeight="medium" _hover={{ textDecoration: 'none', color: 'blue.700' }}>
								View all →
							</Link>
						</Flex>
					</Box>
					<CardBody p={0}>
						{adminorderloading ? (
							<Center py={10}><Spinner color="blue.500" /></Center>
						) : (
							<Box overflowX="auto">
								<Table size="sm" variant="simple">
									<Thead>
										<Tr bg="gray.50">
											<Th color="gray.500" fontSize="xs">Order ID</Th>
											<Th color="gray.500" fontSize="xs">Customer</Th>
											<Th color="gray.500" fontSize="xs">Date</Th>
											<Th color="gray.500" fontSize="xs">Status</Th>
											<Th color="gray.500" fontSize="xs" isNumeric>Amount</Th>
											<Th color="gray.500" fontSize="xs"></Th>
										</Tr>
									</Thead>
									<Tbody>
										{adminorder?.data && adminorder.data.length > 0 ? (
											adminorder.data.slice(0, 5).map((order: any) => (
												<Tr key={order.order_id} _hover={{ bg: 'gray.50' }}>
													<Td>
														<Text fontWeight="medium" fontSize="sm" color="blue.600">
															#{order.order_id}
														</Text>
													</Td>
													<Td>
														<Text fontSize="sm" color="gray.700">{order.email}</Text>
													</Td>
													<Td>
														<Text fontSize="sm" color="gray.500">
															{new Date(order.created_at).toLocaleDateString()}
														</Text>
													</Td>
													<Td>
														<Badge
															colorScheme={statusColor(order.status)}
															px={2}
															py={0.5}
															borderRadius="md"
															fontSize="xs"
														>
															{order.status || 'Processing'}
														</Badge>
													</Td>
													<Td isNumeric>
														<Text fontSize="sm" fontWeight="semibold" color="gray.800">
															{fmt(parseFloat(order.total_amount || '0'))}
														</Text>
													</Td>
													<Td>
														<Link
															as={NavLink}
															to={`/admin/orders/${order.order_id}`}
															fontSize="xs"
															color="blue.500"
															fontWeight="medium"
															_hover={{ color: 'blue.700' }}
														>
															View →
														</Link>
													</Td>
												</Tr>
											))
										) : (
											<Tr>
												<Td colSpan={6} textAlign="center" py={8}>
													<Text color="gray.400" fontSize="sm">No recent orders found.</Text>
												</Td>
											</Tr>
										)}
									</Tbody>
								</Table>
							</Box>
						)}
					</CardBody>
				</Card>
			</Box>
		</>
	);
};

const mapStateToProps = (state: any) => ({
	adminorder: state.order.adminorder,
	adminorderloading: state.order.adminorderloading,
	report: state.sales?.report,
	todaysTotal: state.sales?.todaysTotal,
});

const mapDispatchToProps = {
	getAllAdminOrders,
	getWeeklyySalesReport,
	getTodaysSales,
};

export default connect(mapStateToProps, mapDispatchToProps)(Admin);
